import React, { useEffect, useState } from "react";
import {
  Search,
  Eye,
  Download,
  Clock,
  AlertCircle,
  DollarSign,
  CheckCircle,
  XCircle,
  Upload,
  Calendar,
  X,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import { Panigation } from "@/components/ui/Panigation";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import Spinner from "@/components/Spinner";
import { StatsCard } from "@/components/Dashboard/StatsCard";
import { getCommissionList, approveCommission, rejectCommission, settleDaily } from "@/api/Admin/commission";
import dayjs from "dayjs";
import { Tooltip } from "@/components/ui/Tooltip";

interface CommissionData {
  id: number;
  user_id: number;
  source_user_id?: number;
  gross_amount: number | string;
  tax_fee: number | string;
  net_amount: number | string;
  status: string;
  updated_at: string;
  created_at: string;
  currency: string;
  description: string;
  payable_at: string | null;
  transaction_id: number | null;
  type: string;
  user_info: {
    id: number;
    fullname: string;
    email: string;
    avatar: string | null;
    avatar_url: string | null;
    phone: string | null;
  };
  user_package_id: number;
  is_priority?: boolean;
  bank_info?: {
    bank_name: string;
    account_number: string;
    account_holder: string;
  };
}

const CommissionPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("");
  const [slaFilter, setSlaFilter] = useState("");
  const { t } = useTranslation();

  const [commissionData, setCommissionData] = useState<CommissionData[]>([]);
  const [totalMoney, setTotalMoney] = useState(0);
  const [loading, setLoading] = useState(false);
  const itemsPerPage = 10;

  // Modal states
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [isSettleModalOpen, setIsSettleModalOpen] = useState(false);
  const [selectedCommissionId, setSelectedCommissionId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [approveImage, setApproveImage] = useState<File | null>(null);
  const [settleStartDate, setSettleStartDate] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [settleEndDate, setSettleEndDate] = useState(dayjs().endOf("month").format("YYYY-MM-DD"));
  const [start_date, setStartDate] = useState(dayjs().startOf("month").format("YYYY-MM-DD"));
  const [end_date, setEndDate] = useState(dayjs().endOf("month").format("YYYY-MM-DD"));

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getCommissionList(
        undefined,
        start_date,
        end_date,
        typeFilter || undefined,
        slaFilter ? Number(slaFilter) : undefined,
        100,
        1,
      );

      if (response?.data?.data) {
        setTotalMoney(response.total_commission)
        setCommissionData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching commissions:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchData();
  }, [start_date, end_date, typeFilter, slaFilter]);


  const filteredData = commissionData.filter((commission) => {
    const matchesSearch =
      commission.user_info?.fullname
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      commission.id.toString().includes(searchTerm);

    let matchesSla = true;

    return matchesSearch
  });

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const handleExport = () => {
    console.log("Export data");
  };

  const handleApproveClick = (id: number) => {
    setSelectedCommissionId(id);
    setIsApproveModalOpen(true);
    setApproveImage(null);
  };

  const handleRejectClick = (id: number) => {
    setSelectedCommissionId(id);
    setIsRejectModalOpen(true);
    setRejectReason("");
  };

  const handleSettleClick = () => {
    setSettleStartDate(dayjs().startOf("month").format("YYYY-MM-DD"));
    setSettleEndDate(dayjs().endOf("month").format("YYYY-MM-DD"));
    setIsSettleModalOpen(true);
  };

  const submitApprove = async () => {
    if (!selectedCommissionId || !approveImage) {
      alert("Please select an image");
      return;
    }
    try {
      await approveCommission(selectedCommissionId, approveImage);
      alert("Commission approved successfully");
      setIsApproveModalOpen(false);
      fetchData();
    } catch (error) {
      alert("Failed to approve commission");
    }
  };

  const submitReject = async () => {
    if (!selectedCommissionId || !rejectReason) {
      alert("Please enter a reason");
      return;
    }
    try {
      await rejectCommission(selectedCommissionId, rejectReason);
      alert("Commission rejected successfully");
      setIsRejectModalOpen(false);
      fetchData();
    } catch (error) {
      alert("Failed to reject commission");
    }
  };

  const submitSettle = async () => {
    try {
      await settleDaily(settleStartDate, settleEndDate);
      // Alert is handled in the api function or here
      setIsSettleModalOpen(false);
    } catch (error) {
      // Error alert handled in api function usually, but safe to add here if needed
      console.error(error);
    }
  };

  // calculate SLA
  const calculateSLA = (payableAt: string | null) => {
    if (!payableAt)
      return { isPriority: false, timeRemaining: "N/A", hoursRemaining: 0 };

    const now = dayjs();
    const payableDate = dayjs(payableAt);
    const hoursRemaining = payableDate.diff(now, "hour");
    const minutesRemaining = payableDate.diff(now, "minute") % 60;

    const isPriority = hoursRemaining <= 12 && hoursRemaining > 0;
    const isNormal = hoursRemaining > 12 && hoursRemaining <= 24;

    let timeRemaining = "N/A";
    if (hoursRemaining > 0) {
      timeRemaining = `${hoursRemaining}h ${minutesRemaining}m`;
    } else if (hoursRemaining === 0 && minutesRemaining > 0) {
      timeRemaining = `${minutesRemaining}m`;
    } else {
      timeRemaining = "Đã quá hạn";
    }

    return { isPriority, isNormal, timeRemaining, hoursRemaining };
  };

  //   const getInitials = (name: string) => {
  //     return name
  //       .split(" ")
  //       .map((n) => n[0])
  //       .join("")
  //       .toUpperCase()
  //       .slice(0, 2);
  //   };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Dashboard", path: "/admin/dashboard" },
    { label: t("commissions.title") },
  ];

  // Calculate stats from real data
  const calculateStats = () => {
    const now = dayjs();
    let priorityCount = 0;
    let normalCount = 0;
    let totalAmount = totalMoney;

    commissionData.forEach((commission) => {


      if (commission.is_priority) {
        priorityCount++;
      } else if (commission.payable_at) {
        const payableDate = dayjs(commission.payable_at);
        const hoursRemaining = payableDate.diff(now, "hour");

        if (hoursRemaining > 12 && hoursRemaining <= 24) {
          normalCount++;
        }
      }
    });

    return { priorityCount, normalCount, totalAmount };
  };

  const stats = calculateStats();

  // Stats mock data
  const statsConfig = [
    {
      title: t("commissions.priority"),
      value: stats.priorityCount.toString(),
      change: "+2",
      changeLabel: t("commissions.fromYesterday"),
      trend: "up" as const,
      icon: AlertCircle,
      bgColor: "bg-red-500/10 dark:bg-red-500/20",
      iconColor: "text-red-600 dark:text-red-400",
      chartData: [2, 3, 2, 4, 3, 5, 4, 3, 2, 3, 4, stats.priorityCount],
      chartColor: "#ef4444",
      gradientColor: "#ef4444",
    },
    {
      title: t("commissions.normal"),
      value: stats.normalCount.toString(),
      change: "+1",
      changeLabel: t("commissions.fromYesterday"),
      trend: "up" as const,
      icon: Clock,
      bgColor: "bg-blue-500/10 dark:bg-blue-500/20",
      iconColor: "text-blue-600 dark:text-blue-400",
      chartData: [1, 2, 3, 2, 3, 4, 3, 2, 3, 4, 3, stats.normalCount],
      chartColor: "#3b82f6",
      gradientColor: "#3b82f6",
    },
    {
      title: t("commissions.totalAmount"),
      value: `${(stats.totalAmount / 1000000).toFixed(3)} triệu đ`,
      change: "+15%",
      changeLabel: t("commissions.vsLastMonth"),
      trend: "up" as const,
      icon: DollarSign,
      bgColor: "bg-green-500/10 dark:bg-green-500/20",
      iconColor: "text-green-600 dark:text-green-400",
      chartData: [
        80,
        90,
        85,
        95,
        100,
        105,
        110,
        115,
        120,
        118,
        122,
        stats.totalAmount / 1000000,
      ],
      chartColor: "#22c55e",
      gradientColor: "#22c55e",
    },
  ];

  return (
    <div className="dark:bg-dark-bg">
      <Helmet>
        <title>{t("commissions.title")} - Admin Dashboard</title>
      </Helmet>

      <PageHeader
        title={t("commissions.title")}
        description={t("commissions.subtitle")}
        breadcrumbs={breadcrumbs}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {statsConfig.map((stat, index) => (
          <StatsCard
            key={index}
            index={index}
            title={stat.title}
            value={stat.value}
            change={stat.change}
            changeLabel={stat.changeLabel}
            trend={stat.trend}
            icon={stat.icon}
            bgColor={stat.bgColor}
            iconColor={stat.iconColor}
            chartData={stat.chartData}
            chartColor={stat.chartColor}
            gradientColor={stat.gradientColor}
          />
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex md:items-center items-start md:flex-row flex-col gap-3 mb-6">
        <div className="relative flex-1 w-full md:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t("commissions.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2.5 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={start_date}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
          <span className="text-gray-500">-</span>
          <input
            type="date"
            value={end_date}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>
        <div className="flex justify-between items-center gap-2">
          <select
            value={slaFilter}
            onChange={(e) => setSlaFilter(e.target.value)}
            className="pl-4 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t("commissions.allTypes")}</option>
            <option value="1">{t("commissions.priority")}</option>
            <option value="0">{t("commissions.normal")}</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="pl-4 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t("commissions.allSLA")}</option>
            <option value="paid">{t("commissions.paid")}</option>
            <option value="pending">{t("commissions.pending")}</option>

          </select>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSettleClick}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
          >
            <Calendar className="w-4 h-4" />
            {t("commissions.settleDaily", "Quyết toán ngày")}
          </button>
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Download className="w-4 h-4" />
            {t("commissions.export")}
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("commissions.number")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("commissions.member")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("commissions.commission")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("commissions.bank")}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("commissions.sla")}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("commissions.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center">
                    <span className="flex items-center justify-center gap-2">
                      <Spinner size={32} /> {t("common.loading")}...
                    </span>
                  </td>
                </tr>
              ) : paginatedData.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-4 text-center text-sm text-gray-700 dark:text-gray-300"
                  >
                    {t("commissions.noData")}
                  </td>
                </tr>
              ) : (
                paginatedData.map((commission, idx) => {
                  const slaInfo = calculateSLA(commission.payable_at);

                  return (
                    <tr
                      key={commission.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        {(currentPage - 1) * itemsPerPage + idx + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              #{commission.user_info?.id}
                            </div>
                            <div className="text-sm text-gray-900 dark:text-white">
                              {commission.user_info?.fullname}
                            </div>
                            {commission.is_priority && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                {t("commissions.priorityBadge")}
                              </span>
                            )}
                            {!commission.is_priority && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                                {t("commissions.normalBadge")}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1 min-w-[200px] max-w-[250px]">
                          <div className="text-xs text-gray-500 dark:text-gray-400 mb-1 break-words">
                            {commission.description ||
                              `${Number(commission.gross_amount) > 20000000 ? "15" : "8"} ${t("commissions.dong")}`}
                          </div>
                          <div className="flex justify-between items-center text-sm text-gray-900 dark:text-white">
                            <span>{t("commissions.total")}:</span>
                            <span>
                              {Number(commission.gross_amount).toLocaleString()}{" "}
                              đ
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                            <span>{t("commissions.tax")}:</span>
                            <span>
                              -{Number(commission.tax_fee).toLocaleString()} đ
                            </span>
                          </div>
                          <div className="flex justify-between items-center text-sm font-medium text-gray-900 dark:text-white">
                            <span>{t("commissions.netAmount")}:</span>
                            <span>
                              {Number(commission.net_amount).toLocaleString()} đ
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {commission.bank_info ? (
                          <div className="text-sm">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {commission.bank_info.bank_name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {commission.bank_info.account_number}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {commission.bank_info.account_holder}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-orange-600 dark:text-orange-400">
                            {t("commissions.missingBankInfo")}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="text-sm">
                          <div className="text-gray-500 dark:text-gray-400 text-xs">
                            {t("commissions.approved")}:{" "}
                            {commission.payable_at
                              ? dayjs(commission.payable_at).format(
                                "DD/MM/YYYY",
                              )
                              : "N/A"}
                          </div>
                          <div
                            className={`font-medium mt-1 ${commission.status === "pending" && slaInfo.hoursRemaining < 0 ? "text-red-600 dark:text-red-400" : "text-orange-600 dark:text-orange-400"}`}
                          >
                            {commission.status === "pending" && slaInfo.hoursRemaining < 0 && (
                              <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                {slaInfo.timeRemaining}
                              </span>
                            )}
                            {
                              commission.status === "pending" && slaInfo.hoursRemaining >= 0 && (
                                <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400">
                                  {slaInfo.timeRemaining}
                                </span>
                              )
                            }
                            {commission.status === "pending" && slaInfo.hoursRemaining >= 0 && (
                              <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                                {t("commissions.pending")}
                              </span>
                            )
                            }
                            {commission.status === "waiting_tax_check" && slaInfo.hoursRemaining < 0 && (
                              <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                {t("commissions.pending")}
                              </span>
                            )}
                            {commission.status === "waiting_tax_check" && slaInfo.hoursRemaining >= 0 && (
                              <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                                {t("commissions.pending")}
                              </span>
                            )}
                            {commission.status === "paid" && (
                              <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                {t("commissions.paid")}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Tooltip text={t("commissions.viewDetail")}>
                            <button className="inline-flex items-center justify-center p-2 text-blue-600 hover:text-white dark:text-blue-400 hover:bg-blue-600 dark:hover:bg-blue-600 rounded-md transition-colors">
                              <Eye className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip text={t("commissions.payment")}>
                            <button
                              onClick={() => handleApproveClick(commission.id)}
                              className="inline-flex items-center justify-center p-2 text-green-600 hover:text-white dark:text-green-400 hover:bg-green-600 dark:hover:bg-green-600 rounded-md transition-colors"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          </Tooltip>
                          <Tooltip text={t("commissions.reject", "Từ chối")}>
                            <button
                              onClick={() => handleRejectClick(commission.id)}
                              className="inline-flex items-center justify-center p-2 text-red-600 hover:text-white dark:text-red-400 hover:bg-red-600 dark:hover:bg-red-600 rounded-md transition-colors"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          </Tooltip>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <Panigation
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Approve Modal */}
      {isApproveModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t("commissions.approveTitle", "Xác nhận thanh toán")}</h3>
              <button onClick={() => setIsApproveModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <p className="mb-4 text-gray-600 dark:text-gray-300">
                Vui lòng tải lên hình ảnh giao dịch để xác nhận thanh toán.
              </p>
              <div className="mt-2">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" />
                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400"><span className="font-semibold">Click to upload</span></p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={(e) => setApproveImage(e.target.files ? e.target.files[0] : null)}
                  />
                </label>
                {approveImage && <p className="mt-2 text-sm text-green-600">{approveImage.name}</p>}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setIsApproveModalOpen(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                {t("common.cancel", "Hủy")}
              </button>
              <button
                onClick={submitApprove}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition"
              >
                {t("common.confirm", "Xác nhận")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t("commissions.rejectTitle", "Từ chối thanh toán")}</h3>
              <button onClick={() => setIsRejectModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <p className="mb-4 text-gray-600 dark:text-gray-300">
                Nhập lý do từ chối thanh toán hoa hồng này.
              </p>
              <textarea
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Nhập lý do..."
              />
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setIsRejectModalOpen(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                {t("common.cancel", "Hủy")}
              </button>
              <button
                onClick={submitReject}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition"
              >
                {t("common.reject", "Từ chối")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Settle Daily Modal */}
      {isSettleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">{t("commissions.settleTitle", "Quyết toán ngày")}</h3>
              <button onClick={() => setIsSettleModalOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                <X size={20} />
              </button>
            </div>
            <div className="p-6">
              <p className="mb-4 text-gray-600 dark:text-gray-300">
                Chọn khoảng thời gian để thực hiện quyết toán hoa hồng.
              </p>
              <div className="flex flex-row items-center gap-2">
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                  value={settleStartDate}
                  onChange={(e) => setSettleStartDate(e.target.value)}
                />
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-colors"
                  value={settleEndDate}
                  onChange={(e) => setSettleEndDate(e.target.value)}
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setIsSettleModalOpen(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                {t("common.cancel", "Hủy")}
              </button>
              <button
                onClick={submitSettle}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition"
              >
                {t("common.confirm", "Xác nhận")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionPage;
